import { ref } from 'vue'
import { ApiError, loginUnified, type UnifiedLoginResponse } from '../api'

export type StaffRole = 'admin' | 'proctor'

const tokenKey = (role: StaffRole) => `oap_staff_${role}`

export function readStaffToken(role: StaffRole): string {
  try {
    return localStorage.getItem(tokenKey(role)) ?? ''
  } catch {
    return ''
  }
}

export function writeStaffToken(role: StaffRole, value: string): void {
  try {
    if (value) localStorage.setItem(tokenKey(role), value)
    else localStorage.removeItem(tokenKey(role))
  } catch {
    // Storage may be blocked; the screen then asks for the login again after a reload.
  }
}

/** Send a signed-in staff member to their own screen. */
export function goToStaffScreen(role: StaffRole, token: string): void {
  writeStaffToken(role, token)
  window.location.assign(`/${role}`)
}

/**
 * State for the shared login form on a staff screen. Only the expected role stays here;
 * another role is forwarded to its own screen, a participant is turned away.
 */
export function useStaffLogin(expected: StaffRole, onSuccess: (token: string, displayName: string) => void) {
  const busy = ref(false)
  const error = ref('')
  const retryUntil = ref<number | null>(null)

  async function submit(identifier: string, secret: string): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = ''
    retryUntil.value = null
    try {
      const result: UnifiedLoginResponse = await loginUnified({ identifier, secret })
      if (result.role === 'student') {
        error.value = `Akun ini bukan akun ${expected}. Peserta masuk lewat halaman ujian.`
      } else if (result.role !== expected) {
        goToStaffScreen(result.role, result.token)
      } else {
        onSuccess(result.token, result.staff.display_name)
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) retryUntil.value = Date.now() + (e.retryAfterSeconds ?? 5) * 1000
      else error.value = e instanceof Error ? e.message : 'Login gagal.'
    } finally {
      busy.value = false
    }
  }

  return { busy, error, retryUntil, submit }
}

export type StaffLoginResponse = Extract<UnifiedLoginResponse, { role: StaffRole }>

/** True when the unified login answered for a staff account rather than a participant. */
export function isStaffLogin(result: object): result is StaffLoginResponse {
  return 'role' in result && (result.role === 'admin' || result.role === 'proctor')
}

// Page path of each staff screen; the proctor page keeps its Indonesian address (pengawas.html).
const STAFF_PAGES: Record<StaffRole, string> = { admin: '/admin', proctor: '/pengawas' }

/**
 * Home screen of a staff member who is already signed in, or null. `except` skips the
 * screen we are already on, so a stale token there cannot cause a redirect loop.
 */
export function staffHome(except?: StaffRole): string | null {
  for (const role of ['admin', 'proctor'] as const) {
    if (role !== except && readStaffToken(role)) return STAFF_PAGES[role]
  }
  return null
}
